import { CSSProperties, Fragment, useCallback, useMemo, useState } from 'react';
import { useSearchParams, generatePath, useNavigate, useParams } from 'react-router-dom';
import classNames from 'classnames';

import { TabsProps, TTabOption } from './tabs.types';
import OptionalComponent from '@ui/optional-component/optional-component';
import useValidateTabs from './use-validate-tabs.hook';
import useTrackQuerySearch from './use-track-query-search.hook';
import useTrackUrlParam from './use-track-url-param.hook';
import useTabsEmitters from './use-tabs-emitters';
import randomString from '@/utils/random-string';

import styles from './tabs.module.css';

const Tabs = <T,>({
  id: propsId,
  tabs: propsTabs,
  height,
  initialIndex = 0,
  renderTabItem,
  renderTabContent,
  selectedUnderlineColor = `var(--local-bttn-prim-default-bg)`,
  onTabChange,
  trackUrlParam,
  bottomBorder,
  alignLabel = 'center',
  labelClassName = '',
  containerClassName = '',
  ref,
}: TabsProps<T>) => {
  const [activeIndex, setActiveIndex] = useState(initialIndex || 0);

  const id = useMemo(() => propsId || randomString(7), [propsId]);

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useParams();
  const navigate = useNavigate();

  const tabs = useValidateTabs(propsTabs);

  useTabsEmitters({ id, activeIndex, tabs: tabs || [], setActiveIndex });

  useTrackQuerySearch({
    tabs,
    trackUrlParam,
    setActiveIndex,
  });

  useTrackUrlParam({
    tabs,
    trackUrlParam,
    activeIndex,
    setActiveIndex,
  });

  const handleClickedAsQuery = useCallback(
    (tab: TTabOption) => {
      const { type, key } = trackUrlParam || {};
      if (type === 'query' && key) {
        const urlSearchParams = new URLSearchParams(searchParams);
        urlSearchParams.set(key, tab.value);
        setSearchParams(urlSearchParams);
      }
    },
    [searchParams, setSearchParams, trackUrlParam]
  );

  const handleClickedAsParam = useCallback(
    (tab: TTabOption) => {
      const { type, key, pathPattern } = trackUrlParam || {};
      if (type === 'param' && key && pathPattern) {
        const url = generatePath(pathPattern, { ...params, [key]: tab.value });
        const urlSearchParams = new URLSearchParams(searchParams)?.toString();
        const updatedSearchParams = urlSearchParams ? `?${urlSearchParams}` : '';
        navigate(`${url}${updatedSearchParams}`);
      }
    },
    [navigate, params, searchParams, trackUrlParam]
  );

  const handleTabClick = useCallback(
    (tab: TTabOption<T>, index: number) => {
      setActiveIndex(index);
      onTabChange?.(tab, index);
      handleClickedAsQuery(tab);
      handleClickedAsParam(tab);
    },
    [onTabChange, handleClickedAsQuery, handleClickedAsParam]
  );

  if (!tabs || tabs.length === 0) return null;

  const activeTab = tabs[activeIndex];

  return (
    <>
      <div
        className={`flex fd-row ${styles['tab-container']} ${containerClassName || ''}`}
        ref={ref}
        style={
          {
            ...(height ? { '--tabsHeight': height } : {}),
          } as CSSProperties
        }
      >
        {tabs.map((tab, index) => {
          const { label, value } = tab;
          if (renderTabItem) {
            return (
              <Fragment key={value}>
                {renderTabItem(tab, index, {
                  activeIndex,
                  setActiveIndex,
                  handleTabClick,
                })}
              </Fragment>
            );
          }

          return (
            <div
              key={value}
              data-testid={`tab-${value}`}
              className={classNames(styles['tab-item'], {
                [styles['active']]: index === activeIndex,
              })}
            >
              <button
                className={`clear-button ${styles['tab-button']} ${tabs.length - 1 === index ? styles[`last-tab`] : ''}`}
                onClick={() => handleTabClick(tab, index)}
                style={
                  {
                    '--selectedUnderlineColor': selectedUnderlineColor,
                  } as CSSProperties
                }
              >
                <div className={`flex-column jc-center ai-center ${styles['tab-btn-content-wrapper']}`}>
                  <div
                    className={classNames(
                      {
                        S1: !renderTabContent,
                      },
                      styles['tab-btn-content'],
                      styles[`align-label-${alignLabel}`],
                      labelClassName
                    )}
                  >
                    {renderTabContent ? renderTabContent(tab, index, { activeIndex, setActiveIndex }) : label}
                  </div>
                  <div className={`radius-12 ${styles['underline']}`} />
                </div>
              </button>
            </div>
          );
        })}
      </div>
      <OptionalComponent display={!!bottomBorder}>
        <div style={{ borderBottom: bottomBorder }} />
      </OptionalComponent>
      <OptionalComponent display={!!activeTab.content}>{activeTab.content || <></>}</OptionalComponent>
    </>
  );
};

export default Tabs;
